import React, { useState, useRef } from 'react';
import { Send, Image, X, Loader } from 'lucide-react';
import OpenAI from "openai";
import Header from "../../components/Header";

const openai = new OpenAI({ 
  apiKey: "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", 
  dangerouslyAllowBrowser: true 
});

export default function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const fileInputRef = useRef(null);

  const handleSendMessage = async () => {
    if (!inputText.trim() && selectedImages.length === 0) return;

    // Add user message to state
    const userMessage = {
      role: 'user',
      content: inputText,
      images: selectedImages,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Prepare images for API
      const imagePromises = selectedImages.map(file => {
        return new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      });

      const imagesDataUrls = await Promise.all(imagePromises);

      // Prepare message content for API
      const messageContent = [
        { type: "text", text: inputText }
      ];

      // Add images to content if present
      if (imagesDataUrls.length > 0) {
        messageContent.push(...imagesDataUrls.map(image => ({
          type: "image_url",
          image_url: { url: image, detail: "high" }
        })));
      }

      // Prepare previous messages with proper image handling
      const previousMessages = await Promise.all(messages.map(async msg => {
        if (msg.role === 'user') {
          const content = [{ type: "text", text: msg.content }];
          
          if (msg.images && msg.images.length > 0) {
            // Convert images to base64
            const imagePromises = msg.images.map(file => {
              return new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(file);
              });
            });
            
            const base64Images = await Promise.all(imagePromises);
            content.push(...base64Images.map(image => ({
              type: "image_url",
              image_url: { url: image, detail: "high" }
            })));
          }
          
          return {
            role: msg.role,
            content: content
          };
        }
        
        return {
          role: msg.role,
          content: msg.content
        };
      }));

      // Call OpenAI API
      const completion = await openai.chat.completions.create({
        model: "gpt-4.1-2025-04-14",
        messages: [
          ...previousMessages,
          {
            role: "user",
            content: messageContent
          }
        ]
      });
      console.log(messageContent)
      const response = completion.choices[0].message.content;
      console.log("-------------")
      console.log(response)
      // Add assistant's response to messages
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      }]);

      // Clear input and images after sending
      setInputText('');
      setSelectedImages([]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Error: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderFormattedResponse = (content) => {
    if (!content) return null;
    
    // Convert markdown tables to HTML tables
    let formattedText = content;
    
    // Find markdown tables and convert them to HTML tables
    const tableRegex = /\|(.+)\|[\r\n]+\|([-:\s|]+)\|[\r\n]+((?:\|.+\|[\r\n]+)+)/g;
    formattedText = formattedText.replace(tableRegex, (match) => {
      const rows = match.split('\n').filter(row => row.trim() !== '');
      
      if (rows.length < 3) return match;
      
      const headerRow = rows[0];
      const headerCells = headerRow.split('|').filter(cell => cell.trim() !== '');
      const headerHTML = `<tr>${headerCells.map(cell => `<th>${cell.trim()}</th>`).join('')}</tr>`;
      
      const dataRowsHTML = rows.slice(2).map(row => {
        const cells = row.split('|').filter(cell => cell.trim() !== '');
        return `<tr>${cells.map(cell => `<td>${cell.trim()}</td>`).join('')}</tr>`;
      }).join('');
      
      return `<table class="markdown-table"><thead>${headerHTML}</thead><tbody>${dataRowsHTML}</tbody></table>`;
    });
    
    // Process other markdown elements
    formattedText = formattedText
      .replace(/#{1,6}\s+(.*)/g, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- (.*)/gm, '<li>$1</li>')
      .replace(/\n\n/g, '<br/>');
    
    // Add CSS for tables
    const tableStyles = `
      <style>
        .markdown-table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }
        .markdown-table th, .markdown-table td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        .markdown-table th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .markdown-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
      </style>
    `;
    
    return <div dangerouslySetInnerHTML={{ __html: tableStyles + formattedText }} />;
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <Header title="Orca Chat" />

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 text-center">
              Send a message to start the conversation.
            </p>
          </div>
        )}

        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`max-w-3xl mx-auto ${message.role === 'user' ? 'text-right ml-auto' : 'text-left mr-auto'}`}
          >
            <div 
              className={`inline-block rounded-lg px-4 py-3 ${
                message.role === 'user' ? 'bg-blue-500 text-white' : 
                message.role === 'system' ? 'bg-red-500 text-white' : 'bg-white text-gray-800 shadow'
              }`}
            >
              {message.role === 'assistant' ? renderFormattedResponse(message.content) : message.content}
            </div>
            
            {/* Display images in user message */}
            {message.role === 'user' && message.images && message.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 justify-end">
                {message.images.map((img, imgIndex) => (
                  <div key={imgIndex} className="relative w-16 h-16">
                    <img 
                      src={URL.createObjectURL(img)} 
                      alt={`Image ${imgIndex}`} 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                ))}
              </div>
            )}
            
            <div className="text-xs text-gray-500 mt-1">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-center items-center">
            <div className="bg-white rounded-lg shadow px-4 py-2 flex items-center">
              <Loader className="w-4 h-4 text-gray-500 animate-spin mr-2" />
              <span className="text-gray-500">Processing...</span>
            </div>
          </div>
        )}
      </div>

      {/* Selected images preview */}
      {selectedImages.length > 0 && (
        <div className="bg-white border-t px-6 py-2">
          <div className="flex flex-wrap gap-2">
            {selectedImages.map((img, index) => (
              <div key={index} className="relative w-16 h-16">
                <img 
                  src={URL.createObjectURL(img)} 
                  alt={`Selected ${index}`} 
                  className="w-full h-full object-cover rounded"
                />
                <button 
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="bg-white border-t px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end space-x-2">
            <button 
              onClick={() => fileInputRef.current.click()} 
              className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <Image className="w-5 h-5" />
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImageSelect} 
              multiple 
              accept="image/*" 
              className="hidden" 
            />
            
            <div className="flex-1 border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Type your message..."
                className="w-full px-4 py-2 focus:outline-none resize-none"
                rows={1}
                style={{ minHeight: '44px', maxHeight: '200px' }}
              />
            </div>
            
            <button 
              onClick={handleSendMessage}
              disabled={isLoading} 
              className={`p-3 rounded-full ${
                isLoading || (!inputText.trim() && selectedImages.length === 0)
                  ? 'bg-gray-300 text-gray-500'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}